import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { GeoProvider } from "@/lib/geo";
import { useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { SessionTimeout } from "@/components/SessionTimeout";

import Catalog from "@/pages/Catalog";
import HelpCenter from "@/pages/HelpCenter";
import WorkshopDetail from "@/pages/WorkshopDetail";
import PaymentResult from "@/pages/PaymentResult";
import MyTickets from "@/pages/MyTickets";
import GiftClaim from "@/pages/GiftClaim";
import MyPrizes from "@/pages/MyPrizes";
import MySessionsPage from "@/pages/MySessionsPage";
import SavedReservations from "@/pages/SavedReservations";
import MyMessages from "@/pages/MyMessages";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import RoleChooser from "@/pages/RoleChooser";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import BusinessDashboard from "@/pages/BusinessDashboard";
import MaestroProfile from "@/pages/MaestroProfile";
import AlumnoProfile from "@/pages/AlumnoProfile";
import AdminDashboard from "@/pages/AdminDashboard";
import MisMaestros from "@/pages/MisMaestros";
import AppStart from "@/pages/AppStart";
import { AlumnoBottomNav } from "@/components/AlumnoBottomNav";
import { OfflineBanner } from "@/components/OfflineBanner";
import { AttendanceSync } from "@/components/AttendanceSync";
import { AccountSync } from "@/components/AccountSync";
import { DiscountWelcome } from "@/components/DiscountWelcome";

function Shell() {
  const location = useLocation();
  const { user } = useAuth();
  // Google OAuth callback: process session_id synchronously before anything else.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  // Usuario de Google sin perfil elegido: forzamos la elección de Maestro/Alumno.
  if (user && user.needs_role && location.pathname !== "/elegir-perfil")
    return <Navigate to="/elegir-perfil" replace />;

  return (
    <>
      <Navbar />
      <Routes>
        <Route path="/" element={<Catalog />} />
        <Route path="/explorar" element={<Catalog />} />
        <Route path="/ayuda" element={<HelpCenter />} />
        <Route path="/elegir-perfil" element={<RoleChooser />} />
        <Route path="/taller/:id" element={<WorkshopDetail />} />
        <Route path="/maestro/:id" element={<MaestroProfile />} />
        <Route path="/pago/:status" element={<PaymentResult />} />
        <Route path="/mis-tickets" element={<MyTickets />} />
        <Route path="/regalo/:token" element={<GiftClaim />} />
        <Route path="/mis-creditos" element={<MyPrizes />} />
        <Route path="/mis-sesiones" element={<MySessionsPage />} />
        <Route path="/mis-premios" element={<Navigate to="/mis-creditos" replace />} />
        <Route path="/mis-reservas" element={<SavedReservations />} />
        <Route path="/mensajes" element={<MyMessages />} />
        <Route path="/mis-maestros" element={<MisMaestros />} />
        <Route path="/app" element={<AppStart />} />
        <Route path="/mi-perfil" element={
          <ProtectedRoute roles={["customer"]}><AlumnoProfile /></ProtectedRoute>
        } />
        <Route path="/alumno/perfil" element={<Navigate to="/mi-perfil" replace />} />
        <Route path="/login" element={<Login mode="login" />} />
        <Route path="/registro" element={<Login mode="register" />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/maestro" element={
          <ProtectedRoute roles={["business"]}><BusinessDashboard /></ProtectedRoute>
        } />
        <Route path="/negocio" element={<Navigate to="/maestro" replace />} />
        <Route path="/admin" element={
          <ProtectedRoute roles={["super_admin"]}><AdminDashboard /></ProtectedRoute>
        } />
      </Routes>
      <Footer />
      <AlumnoBottomNav />
      <OfflineBanner />
      <AttendanceSync />
      <AccountSync />
      <DiscountWelcome />
      <SessionTimeout />
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <GeoProvider>
            <Shell />
          </GeoProvider>
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
